import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface RiskCustomer {
  id: string
  name: string
  risk_score: number
  risk_factors: string[]
  predicted_churn_date: string
  ltv: number
  recommended_actions: string[]
}

interface RiskFactor {
  id: string
  name: string
  weight: number
  affected_customers: number
  trend: string
}

interface Segment {
  id: string
  name: string
  total_customers: number
  at_risk: number
  risk_percentage: number
  avg_risk_score: number
}

interface Statistics {
  at_risk_customers: number
  revenue_at_risk: number
  model_accuracy: number
  prevented_churns: number
}

export default function ChurnPredictionPage() {
  const [predictions, setPredictions] = useState<RiskCustomer[]>([])
  const [factors, setFactors] = useState<RiskFactor[]>([])
  const [segments, setSegments] = useState<Segment[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [predictionsRes, factorsRes, segmentsRes, statsRes] = await Promise.all([
        fetch('/api/churnprediction/predictions'),
        fetch('/api/churnprediction/factors'),
        fetch('/api/churnprediction/segments'),
        fetch('/api/churnprediction/statistics')
      ])
      if (predictionsRes.ok) setPredictions(await predictionsRes.json())
      if (factorsRes.ok) setFactors(await factorsRes.json())
      if (segmentsRes.ok) setSegments(await segmentsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching churn prediction data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getRiskColor = (score: number) => {
    if (score >= 80) return 'text-red-600'
    if (score >= 50) return 'text-orange-600'
    return 'text-yellow-600'
  }

  const getTrendIcon = (trend: string) => {
    if (trend === 'increasing') return '↑'
    if (trend === 'decreasing') return '↓'
    return '→'
  }

  const getTrendColor = (trend: string) => {
    if (trend === 'increasing') return 'text-red-600'
    if (trend === 'decreasing') return 'text-green-600'
    return 'text-gray-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Churn Prediction</h1>
          <p className="mt-2 text-gray-600">Phase 225 - AI-powered customer churn risk analysis</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At-Risk Customers</p>
              <p className="text-2xl font-bold text-red-600">{statistics.at_risk_customers}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Revenue at Risk</p>
              <p className="text-2xl font-bold text-orange-600">${statistics.revenue_at_risk.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Model Accuracy</p>
              <p className="text-2xl font-bold text-green-600">{statistics.model_accuracy}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Prevented Churns</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.prevented_churns}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">High-Risk Customers</h2>
          </div>
          <div className="p-6 space-y-4">
            {predictions.map((customer) => (
              <div key={customer.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-lg text-gray-900">{customer.name}</p>
                    <p className="text-sm text-gray-500">LTV: ${customer.ltv.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${getRiskColor(customer.risk_score)}`}>
                      {customer.risk_score}%
                    </p>
                    <p className="text-xs text-gray-500">risk score</p>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-sm text-gray-500 mb-1">Risk Factors:</p>
                  <div className="flex flex-wrap gap-1">
                    {customer.risk_factors.map((factor, idx) => (
                      <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">{factor}</span>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-sm text-gray-500">Predicted Churn: <span className="text-orange-600 font-medium">{customer.predicted_churn_date}</span></p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Recommended Actions:</p>
                  <div className="space-y-1">
                    {customer.recommended_actions.map((action, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-green-600">→</span>
                        <span className="text-gray-700">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Churn Risk Factors</h2>
            </div>
            <div className="p-6 space-y-4">
              {factors.map((factor) => (
                <div key={factor.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{factor.name}</span>
                    <span className={`text-sm ${getTrendColor(factor.trend)}`}>
                      {getTrendIcon(factor.trend)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="h-2 bg-red-500 rounded-full" style={{ width: `${factor.weight}%` }} />
                    </div>
                    <span className="text-gray-500 text-sm">{factor.weight}%</span>
                  </div>
                  <p className="text-gray-500 text-sm">{factor.affected_customers} customers affected</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Risk by Segment</h2>
            </div>
            <div className="p-6 space-y-4">
              {segments.map((segment) => (
                <div key={segment.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{segment.name}</span>
                    <span className={`text-lg font-bold ${segment.avg_risk_score >= 60 ? 'text-red-600' : segment.avg_risk_score >= 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {segment.avg_risk_score}%
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">{segment.total_customers} total customers</span>
                    <span className="text-red-600">{segment.at_risk} at risk ({segment.risk_percentage}%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="h-2 bg-red-500 rounded-full" style={{ width: `${segment.risk_percentage}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
